import React, { useState, useEffect } from 'react';
import './ContactDetailView.css';
import { API_BASE_URL } from '@/config/constants';
import { 
  X, Edit, Trash2, Star, User, Mail, Phone, Building2, 
  MapPin, Globe, Tag, Calendar, QrCode, Camera, Download 
} from 'lucide-react';

interface ContactData {
  contactId: number;
  displayName: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  companyName?: string;
  department?: string;
  website?: string;
  notes?: string;
  emails?: Array<{ email: string; type: string; isPrimary?: boolean }>;
  phones?: Array<{ number: string; type: string; isPrimary?: boolean }>;
  addresses?: Array<{ street?: string; city?: string; state?: string; postalCode?: string; country?: string; type?: string }>;
  socialProfiles?: Array<{ platform: string; url: string }>;
  isFavorite: boolean;
  category?: string;
  tags?: string;
  sourceType: string;
  createdAt: string;
}

interface ContactDetailViewProps {
  isOpen: boolean;
  onClose: () => void;
  contact: ContactData | null;
  onEdit: (contact: ContactData) => void;
  onDelete: (contactId: number) => void;
  onToggleFavorite: (contactId: number) => void;
  isDeleting?: boolean;
  getAccessToken: () => Promise<string>;
}

export const ContactDetailView: React.FC<ContactDetailViewProps> = ({
  isOpen,
  onClose,
  contact,
  onEdit,
  onDelete,
  onToggleFavorite,
  isDeleting = false,
  getAccessToken
}) => {
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen || !contact) return null;

  const handleExportVCard = async () => {
    try {
      setIsExporting(true);
      const token = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/wallet/${contact.contactId}/export/vcard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export contact');
      }

      // Get the file content
      const blob = await response.blob();
      
      // Get filename from Content-Disposition header or create from name
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${contact.displayName.replace(/[^a-z0-9]/gi, '_')}.vcf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to export contact:', error);
      alert('Failed to export contact');
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const tagsList = contact.tags ? contact.tags.split(',').map(t => t.trim()) : [];

  return (
    <div className="contact-detail-overlay" onClick={onClose}>
      <div className="contact-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="contact-detail-header">
          <div className="header-top">
            <button 
              onClick={() => onToggleFavorite(contact.contactId)}
              className={`btn-favorite-large ${contact.isFavorite ? 'active' : ''}`}
              title={contact.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star 
                size={24} 
                fill={contact.isFavorite ? 'currentColor' : 'none'}
              />
            </button>
            <button onClick={onClose} className="btn-close-detail">
              <X size={24} />
            </button>
          </div>
          
          <div className="header-content">
            <div className="contact-avatar">
              {(contact.displayName || '?').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            <div className="contact-header-info">
              <h1>{contact.displayName}</h1>
              {contact.jobTitle && <p className="job-title">{contact.jobTitle}</p>}
              {contact.companyName && (
                <p className="company-name">
                  <Building2 size={14} />
                  {contact.companyName}
                  {contact.department && ` • ${contact.department}`}
                </p>
              )}
            </div>
          </div>

          <div className="header-actions">
            <button 
              onClick={handleExportVCard}
              className="btn-action btn-export"
              disabled={isExporting}
            >
              <Download size={18} />
              <span>{isExporting ? 'Exporting...' : 'Export vCard'}</span>
            </button>
            <button 
              onClick={() => onEdit(contact)} 
              className="btn-action btn-edit"
            >
              <Edit size={18} />
              <span>Edit</span>
            </button>
            <button 
              onClick={() => onDelete(contact.contactId)} 
              className="btn-action btn-delete"
              disabled={isDeleting}
            >
              <Trash2 size={18} />
              <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="contact-detail-content">
          {/* Contact Information */}
          {((contact.emails && contact.emails.length > 0) || 
            (contact.phones && contact.phones.length > 0) || 
            contact.website) && (
            <div className="detail-section">
              <h3><Mail size={20} /> Contact Information</h3>
              
              {contact.emails && contact.emails.length > 0 && (
                <div className="detail-group">
                  <label>Email</label>
                  {contact.emails.map((email, index) => (
                    <div key={index} className="detail-item">
                      <Mail size={16} />
                      <a href={`mailto:${email.email}`}>{email.email}</a>
                      <span className="badge-type">{email.type}</span>
                      {email.isPrimary && <span className="badge-primary">Primary</span>}
                    </div>
                  ))}
                </div>
              )}

              {contact.phones && contact.phones.length > 0 && (
                <div className="detail-group">
                  <label>Phone</label>
                  {contact.phones.map((phone, index) => (
                    <div key={index} className="detail-item">
                      <Phone size={16} />
                      <a href={`tel:${phone.number}`}>{phone.number}</a>
                      <span className="badge-type">{phone.type}</span>
                      {phone.isPrimary && <span className="badge-primary">Primary</span>}
                    </div>
                  ))}
                </div>
              )}

              {contact.website && (
                <div className="detail-group">
                  <label>Website</label>
                  <div className="detail-item">
                    <Globe size={16} />
                    <a href={contact.website} target="_blank" rel="noopener noreferrer">
                      {contact.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Address */}
          {contact.addresses && contact.addresses.length > 0 && (
            <div className="detail-section">
              <h3><MapPin size={20} /> Address</h3>
              {contact.addresses.map((addr, index) => (
                <div key={index} className="detail-group">
                  {addr.type && <label>{addr.type}</label>}
                  <div className="address-block">
                    {addr.street && <p>{addr.street}</p>}
                    {(addr.city || addr.state || addr.postalCode) && (
                      <p>{[addr.city, addr.state, addr.postalCode].filter(Boolean).join(', ')}</p>
                    )}
                    {addr.country && <p>{addr.country}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Social Profiles */}
          {contact.socialProfiles && contact.socialProfiles.length > 0 && (
            <div className="detail-section">
              <h3><Globe size={20} /> Social Profiles</h3>
              <div className="detail-group">
                {contact.socialProfiles.map((social, index) => (
                  <div key={index} className="detail-item">
                    <Globe size={16} />
                    <a href={social.url} target="_blank" rel="noopener noreferrer">
                      {social.platform}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {contact.notes && (
            <div className="detail-section">
              <h3>Notes</h3>
              <div className="notes-content">
                <p>{contact.notes}</p>
              </div>
            </div>
          )}

          {/* Organization */}
          <div className="detail-section">
            <h3><Tag size={20} /> Organization</h3>
            
            {contact.category && (
              <div className="detail-group">
                <label>Category</label>
                <div className="category-badge">{contact.category}</div>
              </div>
            )}

            {tagsList.length > 0 && (
              <div className="detail-group">
                <label>Tags</label>
                <div className="tags-display">
                  {tagsList.map((tag, index) => (
                    <span key={index} className="tag-badge">{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="detail-section">
            <h3><Calendar size={20} /> Metadata</h3>
            
            <div className="detail-group">
              <label>Source</label>
              <div className="detail-item">
                {contact.sourceType === 'qr_scan' ? (
                  <>
                    <QrCode size={16} />
                    <span>Scanned from QR Code</span>
                  </>
                ) : contact.sourceType === 'physical_card' ? (
                  <>
                    <Camera size={16} />
                    <span>Scanned from Physical Card</span>
                  </>
                ) : (
                  <>
                    <User size={16} />
                    <span>Manually Added</span>
                  </>
                )}
              </div>
            </div>

            <div className="detail-group">
              <label>Added on</label>
              <div className="detail-item">
                <Calendar size={16} />
                <span>{formatDate(contact.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailView;
